import type { IconName } from '@customerio/pluma-icons';
import type { Newsletter } from './entity';

type SupportedNewsletterChannel = 'email' | 'push' | 'twilio' | 'sms' | 'in_app' | 'inbox' | 'webhook' | 'unset';
type NewsletterMetric =
  | 'failed'
  | 'sent'
  | 'delivered'
  | 'opened'
  | 'human_opened'
  | 'clicked'
  | 'human_clicked'
  | 'bounced'
  | 'converted'
  | 'suppressed'
  | 'spammed'
  | 'unsubscribed'
  | 'clicked_to_opened'
  | 'topic_unsubscribed'
  | 'undeliverable'
  | 'replied';
type NewsletterTestMetric = 'clicked' | 'converted' | 'opened';

export type NewsletterChannelInfo = {
  canAutoSendWinner: boolean;
  canSplit: boolean;
  icon: IconName;
  name: string;
  filterName: string;
  metrics: NewsletterMetric[];
  abMetrics: NewsletterTestMetric[];
  requiresFromIdentity: boolean;
  requiresSubject: boolean;
  settingsPath: string;
  trackingAvailable: boolean;
  ignoresGlobalUnsubscribe?: boolean;
};

const DEFAULT: NewsletterChannelInfo = {
  canAutoSendWinner: false,
  canSplit: false,
  icon: 'newsletter',
  name: 'unknown',
  filterName: 'unknown',
  metrics: ['failed', 'sent', 'opened', 'clicked', 'converted'],
  abMetrics: [],
  requiresFromIdentity: false,
  requiresSubject: false,
  settingsPath: 'workspace-settings',
  trackingAvailable: false,
};

const CHANNEL_INFO = {
  email: {
    canAutoSendWinner: true,
    canSplit: true,
    icon: 'email',
    name: 'email',
    filterName: 'Email',
    metrics: [
      'failed',
      'sent',
      'delivered',
      'opened',
      'human_opened',
      'clicked',
      'human_clicked',
      'bounced',
      'converted',
      'suppressed',
      'spammed',
      'unsubscribed',
      'clicked_to_opened',
      'topic_unsubscribed',
    ],
    abMetrics: ['clicked', 'converted', 'opened'],
    requiresFromIdentity: true,
    requiresSubject: true,
    settingsPath: 'actions.email',
    trackingAvailable: true,
  },
  push: {
    canAutoSendWinner: true,
    canSplit: true,
    icon: 'mobile',
    name: 'push',
    filterName: 'Push',
    metrics: [
      'failed',
      'sent',
      'opened',
      'human_opened',
      'delivered',
      'undeliverable',
      'bounced',
      'converted',
      'suppressed',
      'topic_unsubscribed',
    ],
    abMetrics: ['converted', 'opened'],
    requiresFromIdentity: false,
    requiresSubject: false,
    settingsPath: 'actions.push',
    trackingAvailable: false,
  },
  twilio: {
    canAutoSendWinner: true,
    canSplit: true,
    icon: 'sms',
    name: 'SMS',
    filterName: 'SMS',
    metrics: ['failed', 'sent', 'clicked', 'human_clicked', 'bounced', 'converted', 'replied'],
    abMetrics: ['clicked', 'converted'],
    requiresFromIdentity: false,
    requiresSubject: false,
    settingsPath: 'actions.twilio',
    trackingAvailable: true,
  },
  sms: {
    canAutoSendWinner: true,
    canSplit: true,
    icon: 'sms',
    name: 'SMS',
    filterName: 'SMS',
    metrics: ['failed', 'sent', 'clicked', 'human_clicked', 'bounced', 'converted', 'replied'],
    abMetrics: ['clicked', 'converted'],
    requiresFromIdentity: false,
    requiresSubject: false,
    settingsPath: 'actions.sms-providers',
    trackingAvailable: true,
  },
  in_app: {
    canAutoSendWinner: true,
    canSplit: true,
    icon: 'in-app',
    name: 'in-app',
    filterName: 'In-App',
    metrics: ['failed', 'sent', 'opened', 'human_opened', 'clicked', 'human_clicked', 'converted'],
    abMetrics: ['clicked', 'converted', 'opened'],
    requiresFromIdentity: false,
    requiresSubject: false,
    settingsPath: 'actions.in_app',
    trackingAvailable: false,
    ignoresGlobalUnsubscribe: true,
  },
  inbox: {
    canAutoSendWinner: true,
    canSplit: true,
    icon: 'notifications',
    name: 'inbox',
    filterName: 'Inbox',
    metrics: ['failed', 'sent', 'opened', 'clicked', 'converted'],
    abMetrics: ['clicked', 'converted', 'opened'],
    requiresFromIdentity: false,
    requiresSubject: false,
    settingsPath: 'actions.in_app',
    trackingAvailable: false,
  },
  webhook: {
    canAutoSendWinner: true,
    canSplit: true,
    icon: 'webhook',
    name: 'webhook',
    filterName: 'Webhook',
    metrics: ['failed', 'sent', 'clicked', 'converted'],
    abMetrics: ['clicked', 'converted'],
    requiresFromIdentity: false,
    requiresSubject: false,
    settingsPath: 'actions.webhook',
    trackingAvailable: false,
  },
  unset: DEFAULT,
} satisfies Record<SupportedNewsletterChannel, NewsletterChannelInfo>;

/** @public */
export function getNewsletterChannelInfo(channel: Newsletter['channel'] | undefined): NewsletterChannelInfo {
  switch (channel) {
    case 'email':
    case 'push':
    case 'twilio':
    case 'sms':
    case 'in_app':
    case 'inbox':
    case 'webhook':
    case 'unset':
      return CHANNEL_INFO[channel];
    case '':
    case undefined:
    case 'urban_airship':
    case 'slack':
    case 'line':
    case 'whatsapp':
      return DEFAULT;
    default: {
      const _exhaustive: never = channel;
      return DEFAULT;
    }
  }
}
